import { useEffect, useState, useRef } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ArrowRight, UploadCloud, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import "./motionPreviews.css";

function TransitionPreview({ id }) {
  return (
    <div className={`mp-tile t-${id}`}>
      <div className="mp-photo mp-a" />
      <div className="mp-photo mp-b" />
      {id === "flash" && <div className="mp-flash-veil" />}
    </div>
  );
}

function MovementPreview({ id }) {
  return (
    <div className={`mp-tile m-${id}`}>
      <div className="mp-photo mp-a" />
    </div>
  );
}

const FONTS = ["Outfit", "Cormorant Garamond"];
const TRANSITIONS = [["crossfade", "Crossfade"], ["fade_to_black", "Fade to black"], ["fade_to_white", "Fade to white"], ["flash", "Flash"], ["slide_left", "Slide left"], ["slide_right", "Slide right"], ["slide_up", "Slide up"], ["slide_down", "Slide down"], ["wipe", "Wipe"], ["circle_open", "Circle open"], ["circle_close", "Circle close"], ["dissolve", "Dissolve"], ["pixelize", "Pixelize"], ["radial", "Radial"], ["smooth", "Smooth"], ["diagonal", "Diagonal"], ["zoom", "Zoom"], ["clean_cut", "Clean cut"]];
const SPEEDS = [["fast", "Fast"], ["normal", "Normal"], ["slow", "Slow"]];
const MOVES = [["none", "None"], ["zoom_in", "Gentle zoom in"], ["zoom_out", "Gentle zoom out"], ["zoom_in_fast", "Bold zoom in"], ["pan_right", "Slow pan right"], ["pan_left", "Slow pan left"], ["tilt_up", "Tilt up"], ["tilt_down", "Tilt down"], ["random", "Random cinematic"]];
const FITS = [["crop", "Smart centre crop"], ["blur", "Fit with blurred background"], ["pad", "Fit with solid background"]];
const POSITIONS = [["top_left", "Top left"], ["top_right", "Top right"], ["bottom_left", "Bottom left"], ["bottom_right", "Bottom right"], ["center", "Centre"]];

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-champagne">{label}</Label>
      <div className="flex items-center gap-2">
        <input data-testid={testid} type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 rounded-md bg-transparent border border-navy-border cursor-pointer" />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 bg-navy-deep border-navy-border font-mono text-xs" />
      </div>
    </div>
  );
}

export default function StylePanel({ event, reload, goNext }) {
  const [templates, setTemplates] = useState([]);
  const [booths, setBooths] = useState([]);
  const [design, setDesign] = useState(event.design || {});
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoV, setLogoV] = useState(0);
  const logoRef = useRef();

  useEffect(() => {
    api.get("/meta").then((r) => { setTemplates(r.data.templates); setBooths(r.data.booth_types || []); }).catch(() => {});
  }, []);

  const set = (k, v) => setDesign((d) => ({ ...d, [k]: v }));

  const applyTemplate = (t, persist = false) => {
    setDesign((d) => {
      const next = {
        ...d, font: t.font, primary_color: t.primary_color, secondary_color: t.secondary_color,
        background_color: t.background_color, text_color: t.text_color, transition: t.transition,
        transition_speed: t.transition_speed, movement: t.movement, logo_position: t.logo_position,
        image_duration: t.image_duration, template: t.id,
        intro_title: d.intro_title || t.intro_title, closing_message: t.closing_message,
      };
      if (persist) {
        api.put(`/events/${event.id}`, { design: next })
          .then(() => { toast.success(`${t.name} applied & saved`); reload(); })
          .catch((e) => toast.error(apiError(e)));
      }
      return next;
    });
    if (!persist) toast.success(`${t.name} applied — customise below`);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put(`/events/${event.id}`, { design }); toast.success("Style saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/events/${event.id}/logo`, fd); setLogoV((v) => v + 1); toast.success("Logo uploaded"); reload(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setUploadingLogo(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ivory">Choose a Style</h2>
          <p className="text-muted-foreground text-sm mt-1">Start from a template, then fine-tune the branding. Customising won't change the original template.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="save-style-button" onClick={save} disabled={saving} className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Style</>}
          </Button>
          <Button data-testid="style-next-button" onClick={goNext} variant="outline" className="rounded-full border-gold text-gold hover:bg-gold/10">
            Create Reel <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>

      {/* Booth recommendation */}
      {(() => {
        const booth = booths.find((b) => b.id === event.booth_type);
        if (!booth) return null;
        const rec = templates.find((t) => t.id === booth.template);
        return (
          <div data-testid="booth-recommendation" className="rounded-2xl border border-gold/40 bg-gold/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-champagne text-xs uppercase tracking-wider">Booth: {booth.name}</p>
              <p className="text-ivory text-sm mt-1">{booth.blurb}</p>
              {rec && <p className="text-muted-foreground text-xs mt-1">Recommended template: <span className="text-gold">{rec.name}</span> · pacing {booth.pace}</p>}
            </div>
            {rec && (
              <Button data-testid="apply-recommended-template" onClick={() => applyTemplate(rec, true)} className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold shrink-0">
                <Check className="w-4 h-4 mr-1.5" /> Apply recommended
              </Button>
            )}
          </div>
        );
      })()}

      {/* Templates */}
      <div>
        <h3 className="text-champagne text-sm uppercase tracking-wider mb-3">Templates</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {templates.map((t) => (
            <button key={t.id} data-testid={`template-${t.id}`} onClick={() => applyTemplate(t)}
              className={`text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5 relative overflow-hidden ${design.template === t.id ? "border-gold" : "border-navy-border"}`}
              style={{ background: t.background_color }}>
              <div className="flex gap-1 mb-3">
                <span className="w-4 h-4 rounded-full" style={{ background: t.primary_color }} />
                <span className="w-4 h-4 rounded-full" style={{ background: t.secondary_color }} />
              </div>
              <div className="font-display text-lg leading-tight" style={{ color: t.text_color }}>{t.name}</div>
              <p className="text-[11px] mt-1 opacity-70" style={{ color: t.text_color }}>{t.description}</p>
              {design.template === t.id && <div className="absolute top-2 right-2 bg-gold rounded-full p-1"><Check className="w-3 h-3 text-navy" /></div>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colours & type */}
        <div className="bg-navy-card border border-navy-border rounded-2xl p-5 space-y-4">
          <h3 className="text-champagne text-sm uppercase tracking-wider">Colours & Typography</h3>
          <div className="grid grid-cols-2 gap-3">
            <ColorField label="Primary colour" value={design.primary_color} onChange={(v) => set("primary_color", v)} testid="color-primary" />
            <ColorField label="Secondary colour" value={design.secondary_color} onChange={(v) => set("secondary_color", v)} testid="color-secondary" />
            <ColorField label="Background colour" value={design.background_color} onChange={(v) => set("background_color", v)} testid="color-background" />
            <ColorField label="Text colour" value={design.text_color} onChange={(v) => set("text_color", v)} testid="color-text" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-champagne">Font</Label>
            <Select value={design.font} onValueChange={(v) => set("font", v)}>
              <SelectTrigger data-testid="font-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{FONTS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Motion */}
        <div className="bg-navy-card border border-navy-border rounded-2xl p-5 space-y-4">
          <h3 className="text-champagne text-sm uppercase tracking-wider">Movement & Transitions</h3>

          <div className="space-y-2">
            <Label className="text-xs text-champagne">Transition — tap a preview to choose</Label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="transition-preview-grid">
              {TRANSITIONS.map(([v, l]) => (
                <button key={v} type="button" data-testid={`transition-preview-${v}`} title={l} onClick={() => set("transition", v)}
                  className={`text-left ${design.transition === v ? "mp-selected rounded-lg" : ""}`}>
                  <TransitionPreview id={v} />
                  <span className="block text-[9px] text-champagne mt-1 truncate leading-tight">{l}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-champagne">Photo movement — tap a preview to choose</Label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="movement-preview-grid">
              {MOVES.map(([v, l]) => (
                <button key={v} type="button" data-testid={`movement-preview-${v}`} title={l} onClick={() => set("movement", v)}
                  className={`text-left ${design.movement === v ? "mp-selected rounded-lg" : ""}`}>
                  <MovementPreview id={v} />
                  <span className="block text-[9px] text-champagne mt-1 truncate leading-tight">{l}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-champagne">Transition</Label>
              <Select value={design.transition} onValueChange={(v) => set("transition", v)}>
                <SelectTrigger data-testid="transition-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{TRANSITIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-champagne">Transition speed</Label>
              <Select value={design.transition_speed} onValueChange={(v) => set("transition_speed", v)}>
                <SelectTrigger data-testid="speed-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{SPEEDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-champagne">Photo movement</Label>
              <Select value={design.movement} onValueChange={(v) => set("movement", v)}>
                <SelectTrigger data-testid="movement-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{MOVES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-champagne">Media fit</Label>
              <Select value={design.fit_mode} onValueChange={(v) => set("fit_mode", v)}>
                <SelectTrigger data-testid="fit-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{FITS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Text & branding */}
        <div className="bg-navy-card border border-navy-border rounded-2xl p-5 space-y-4">
          <h3 className="text-champagne text-sm uppercase tracking-wider">Titles</h3>
          <div className="space-y-1.5"><Label className="text-xs text-champagne">Intro title</Label>
            <Input data-testid="intro-title-input" value={design.intro_title || ""} onChange={(e) => set("intro_title", e.target.value)} className="bg-navy-deep border-navy-border" placeholder="Anna & Ben" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-champagne">Closing message</Label>
            <Input data-testid="closing-message-input" value={design.closing_message || ""} onChange={(e) => set("closing_message", e.target.value)} className="bg-navy-deep border-navy-border" placeholder="Thank you" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-champagne">Website address</Label>
            <Input data-testid="website-input" value={design.website || ""} onChange={(e) => set("website", e.target.value)} className="bg-navy-deep border-navy-border" placeholder="clydesideevents.co.uk" /></div>
        </div>

        {/* Logo & watermark */}
        <div className="bg-navy-card border border-navy-border rounded-2xl p-5 space-y-4">
          <h3 className="text-champagne text-sm uppercase tracking-wider">Logo & Watermark</h3>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg bg-navy-deep border border-navy-border flex items-center justify-center overflow-hidden">
              {event.logo_path ? <img key={logoV} src={fileUrl(`/events/${event.id}/logo`) + `&v=${logoV}`} alt="logo" className="w-full h-full object-contain" /> : <span className="text-[10px] text-muted-foreground">No logo</span>}
            </div>
            <input ref={logoRef} data-testid="logo-file-input" type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
            <Button data-testid="upload-logo-button" variant="outline" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} className="border-navy-border">
              {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UploadCloud className="w-4 h-4 mr-1.5" /> Upload logo</>}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-champagne">Show watermark on reels</Label>
            <Switch data-testid="watermark-switch" checked={!!design.watermark} onCheckedChange={(v) => set("watermark", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-champagne">Logo position</Label>
              <Select value={design.logo_position} onValueChange={(v) => set("logo_position", v)}>
                <SelectTrigger data-testid="logo-position-select" className="bg-navy-deep border-navy-border h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{POSITIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-champagne">Watermark opacity: {Math.round((design.watermark_opacity ?? 0.85) * 100)}%</Label>
              <Slider data-testid="opacity-slider" value={[(design.watermark_opacity ?? 0.85) * 100]} min={10} max={100} step={5} onValueChange={([v]) => set("watermark_opacity", v / 100)} className="pt-3" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
